import { FileArchive, FileAudio, FileCode, FileImage, FileSpreadsheet, FileText, FileVideo, Folder } from 'lucide-react'

// File type categories
export const FILE_TYPES = {
  IMAGE: [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'image/bmp',
    'image/tiff',
  ],
  VIDEO: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm', 'video/mkv'],
  AUDIO: ['audio/mp3', 'audio/wav', 'audio/flac', 'audio/aac', 'audio/ogg', 'audio/wma'],
  DOCUMENT: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  SPREADSHEET: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  PRESENTATION: [
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  ARCHIVE: ['application/zip', 'application/x-rar-compressed', 'application/x-7z-compressed', 'application/gzip'],
  CODE: ['text/plain', 'text/html', 'text/css', 'text/javascript', 'application/json', 'application/xml'],
} as const

// Helper function to check if a string is in an array
const includesMimeType = (array: readonly string[], mimeType: string): boolean => {
  return array.includes(mimeType)
}

export const getFileIcon = (mimeType: string, isFolder: boolean = false) => {
  if (isFolder) return Folder

  if (includesMimeType(FILE_TYPES.IMAGE, mimeType)) return FileImage
  if (includesMimeType(FILE_TYPES.VIDEO, mimeType)) return FileVideo
  if (includesMimeType(FILE_TYPES.AUDIO, mimeType)) return FileAudio
  if (includesMimeType(FILE_TYPES.DOCUMENT, mimeType)) return FileArchive
  if (includesMimeType(FILE_TYPES.SPREADSHEET, mimeType)) return FileSpreadsheet
  if (includesMimeType(FILE_TYPES.PRESENTATION, mimeType)) return FileSpreadsheet
  if (includesMimeType(FILE_TYPES.ARCHIVE, mimeType)) return FileArchive
  if (includesMimeType(FILE_TYPES.CODE, mimeType)) return FileCode

  return FileText
}

export const getFileTypeColor = (mimeType: string, isFolder: boolean = false) => {
  if (isFolder) return 'text-blue-500'

  if (includesMimeType(FILE_TYPES.IMAGE, mimeType)) return 'text-green-500'
  if (includesMimeType(FILE_TYPES.VIDEO, mimeType)) return 'text-purple-500'
  if (includesMimeType(FILE_TYPES.AUDIO, mimeType)) return 'text-orange-500'
  if (includesMimeType(FILE_TYPES.DOCUMENT, mimeType)) return 'text-red-500'
  if (includesMimeType(FILE_TYPES.SPREADSHEET, mimeType)) return 'text-green-600'
  if (includesMimeType(FILE_TYPES.PRESENTATION, mimeType)) return 'text-orange-600'
  if (includesMimeType(FILE_TYPES.ARCHIVE, mimeType)) return 'text-yellow-500'
  if (includesMimeType(FILE_TYPES.CODE, mimeType)) return 'text-black-600'

  return 'text-gray-500'
}

export const formatFileSize = (bytes?: number | null): string => {
  if (!bytes || bytes <= 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toUpperCase() || ''
}

/** Display name for a repo file: `name` is what a rename updates; `originalName` is the
 *  immutable upload-time filename and only a fallback. Never use `originalName` first for
 *  display - it goes stale the moment the file is renamed. */
export const getFileDisplayName = (file?: { name?: string | null; originalName?: string | null }): string =>
  file?.name ?? file?.originalName ?? ''

export const isImageFile = (mimeType: string): boolean => {
  return includesMimeType(FILE_TYPES.IMAGE, mimeType)
}

export const isVideoFile = (mimeType: string): boolean => {
  return includesMimeType(FILE_TYPES.VIDEO, mimeType)
}

export const isAudioFile = (mimeType: string): boolean => {
  return includesMimeType(FILE_TYPES.AUDIO, mimeType)
}

export const extractFileNameFromUrl = (url?: string | null): string => {
  if (!url) return ''
  try {
    const u = new URL(url)
    return decodeURIComponent(u.pathname.split('/').pop() || 'File')
  } catch {
    return 'File'
  }
}

/**
 * Convert a File object to a base64 data URL
 * This allows sending files in JSON requests without using multipart/form-data
 * @param file - The File object to convert
 * @returns Promise that resolves to a data URL string (e.g., "data:image/png;base64,...")
 */
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Convert an array of File objects to base64 data URLs
 * @param files - Array of File objects to convert
 * @returns Promise that resolves to an array of data URL strings
 */
export const filesToDataUrls = async (files: File[]): Promise<string[]> => {
  return Promise.all(files.map(fileToDataUrl))
}
